import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Download } from 'lucide-react';
import { generatePDFReport } from './ReportGenerator';
import { downloadAIData } from './AIDataExporter';
import SEO from './SEO';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessmentData, setAssessmentData] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
     
      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch('/api/verify-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });

        const data = await response.json();

        if (data.paid) {
          // Retrieve BOTH formData and comprehensiveReport from session storage
          const storedFormData = sessionStorage.getItem('assessmentFormData');
          const storedReport = sessionStorage.getItem('comprehensiveReport');
          
          if (storedFormData && storedReport) {
            setAssessmentData({
              formData: JSON.parse(storedFormData),
              comprehensiveReport: JSON.parse(storedReport)
            });
          }
        } else {
          alert('Payment not confirmed. Please contact support.');
          navigate('/');
        }
      } catch (error) {
        console.error('Verification error:', error);
        alert('Error verifying payment. Please contact info@firststepehs.com');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  const handleDownloadReport = () => {
    if (!assessmentData) {
      alert('Assessment data not found. Please contact support at info@firststepehs.com');
      return;
    }

    try {
      // Pass BOTH parameters as your ReportGenerator expects
      generatePDFReport(assessmentData.comprehensiveReport, assessmentData.formData);
    } catch (error) {
      console.error('Report generation error:', error);
      alert('Error generating report. Please contact support at info@firststepehs.com');
    }
  };

  const handleDownloadAIData = () => {
    if (!assessmentData) {
      alert('Assessment data not found. Please contact support at info@firststepehs.com');
      return;
    }

    try {
      // Pass BOTH parameters as your AIDataExporter expects
      downloadAIData(assessmentData.comprehensiveReport, assessmentData.formData);
    } catch (error) {
      console.error('AI data export error:', error);
      alert('Error exporting data. Please contact support at info@firststepehs.com');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Payment Successful - First Step EHS"
        description="Your EHS assessment payment was successful. Download your comprehensive report."
        canonical="https://firststepehs.com/payment-success"
      />

    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          <CheckCircle className="text-green-600 mx-auto mb-6" size={64} />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Thank you for your purchase. Your comprehensive report is ready.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 text-left">
            <h3 className="font-semibold text-gray-900 mb-3">What's Included:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>✅ Comprehensive HTML Report (download below)</li>
              <li>✅ AI-Ready JSON Data Export</li>
              <li>✅ Facility-by-facility regulatory analysis</li>
              <li>✅ Implementation roadmap with cost estimates</li>
              <li>✅ Citation index with regulatory links</li>
            </ul>
          </div>

          {!assessmentData && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> Assessment data not found in session. If you need your report, please contact us at info@firststepehs.com with your payment confirmation.
              </p>
            </div>
          )}

          <div className="space-y-4">
            <p className="text-gray-700">
              <strong>Important:</strong> Please download your reports now.
            </p>
           
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleDownloadReport}
                disabled={!assessmentData}
                className={`flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold transition-colors ${
                  assessmentData 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Download size={20} />
                Download HTML Report
              </button>
              <button
                onClick={handleDownloadAIData}
                disabled={!assessmentData}
                className={`flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold transition-colors ${
                  assessmentData 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Download size={20} />
                Download AI Data
              </button>
            </div>

            <button
              onClick={() => navigate('/')}
              className="mt-6 text-blue-600 hover:underline"
            >
              Return to Home
            </button>
          </div>

          <div className="mt-8 pt-8 border-t text-sm text-gray-600">
            <p>Questions? Contact us at <a href="mailto:info@firststepehs.com" className="text-blue-600 hover:underline">info@firststepehs.com</a> or (470) 239-0387</p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PaymentSuccess;